import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/shared/foundation/primitives/Button";

import { FieldTypeIcon } from "./fieldTypeIcons";
import type { FilterDefinition } from "./FilterDefinition";
import styles from "./FilterForm.module.css";
import {
  effectiveComparators,
  type Filter,
  filterSchema,
  getDefaultsForVariant,
  isValueless,
} from "./FilterSchema";
import { OperatorPicker } from "./OperatorPicker";
import { BooleanValueInput } from "./valueInputs/BooleanValueInput";
import { DateValueInput } from "./valueInputs/DateValueInput";
import { NumericValueInput } from "./valueInputs/NumericValueInput";
import { OptionsValueInput } from "./valueInputs/OptionsValueInput";
import { StringValueInput } from "./valueInputs/StringValueInput";

interface FilterFormProps {
  /** The field the user picked in the left pane. */
  selectedField: FilterDefinition;

  /** Fired when the user clicks Add filter (or Cmd/Ctrl+Enter on a non-text input). */
  onCommit: (filter: Filter) => void;

  /** Fired when the user clicks Cancel. */
  onCancel: () => void;

  /**
   * Seeds the form with an existing filter's comparator and values (the
   * chip-edit flow). Must match `selectedField`'s type/field. When absent,
   * the form starts from the field's blank defaults (the add flow).
   */
  initialFilter?: Filter;

  /** Submit button label. Defaults to `"Add filter"`; the edit popover
   *  passes `"Update filter"`. */
  submitLabel?: string;
}

/**
 * The right pane of the filter menu — and the entire form.
 */
export function FilterForm({
  selectedField,
  onCommit,
  onCancel,
  initialFilter,
  submitLabel = "Add filter",
}: FilterFormProps) {
  const form = useForm<Filter>({
    resolver: zodResolver(filterSchema),
    defaultValues: initialFilter ?? getDefaultsForVariant(selectedField),
    // Continuous validation so the Add filter button reflects validity
    // live as the user fills the value input(s).
    mode: "onChange",
  });

  const filter = form.watch();

  const setValues = (next: Filter["values"]) =>
    form.setValue("values", next, {
      shouldValidate: true,
      shouldDirty: true,
    });

  return (
    <form
      // No filter fields need browser provided autocomplete
      autoComplete="off"
      className={styles.panel}
      onSubmit={(event) => {
        void form.handleSubmit((values) => onCommit(values))(event);
      }}
    >
      <section className={styles.content}>
        <FilterFormHeader field={selectedField} />
        <section className={styles.section} aria-label="Operator">
          <h4 className={styles.sectionLabel}>Operator</h4>
          <Controller
            name="comparator"
            control={form.control}
            render={({ field }) => (
              <OperatorPicker
                fieldType={selectedField.type}
                value={field.value}
                onChange={(next) => {
                  field.onChange(next);
                  // Clear the value if the user selects a valueless operator
                  if (isValueless(next)) {
                    setValues([]);
                  }
                }}
                operators={effectiveComparators(selectedField)}
              />
            )}
          />
        </section>
        {!isValueless(filter.comparator) && (
          <section className={styles.section} aria-label="Value">
            <h4 className={styles.sectionLabel}>Value</h4>
            {renderValueInput(filter, selectedField, setValues)}
          </section>
        )}
      </section>
      <div className={styles.footer}>
        <Button
          type="button"
          variant="text"
          size="small"
          onClick={onCancel}
          className={styles.footerAction}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={!form.formState.isValid}
          className={styles.footerAction}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function FilterFormHeader({ field }: { field: FilterDefinition }) {
  return (
    <header className={styles.header}>
      <div className={styles.headerGroup}>
        {field.icon ? (
          <span className={styles.headerIcon}>{field.icon}</span>
        ) : (
          <FieldTypeIcon type={field.type} className={styles.headerIcon} />
        )}
        <h3 className={styles.headerName}>{field.name}</h3>
      </div>
      <span className={styles.headerType}>{field.type}</span>
    </header>
  );
}

function renderValueInput(
  filter: Filter,
  selectedField: FilterDefinition,
  onChange: (next: Filter["values"]) => void,
) {
  switch (filter.type) {
    case "string":
      return (
        <StringValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
        />
      );
    // `metric` shares the numeric value input. FilterForm isn't used to build
    // metric filters today (the metrics pane is bespoke), but the case keeps
    // the switch exhaustive over `Filter["type"]`.
    case "numeric":
    case "metric":
      return (
        <NumericValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
        />
      );
    case "date":
      return (
        <DateValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
        />
      );
    case "boolean":
      return (
        <BooleanValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
        />
      );
    case "set":
    case "enum":
      return (
        <OptionsValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
          // `filter.type` is seeded from the definition and the form remounts
          // per field, so an options-backed filter always has an options-backed
          // definition. The `[]` fallback is unreachable; it only narrows the type.
          options={"options" in selectedField ? selectedField.options : []}
        />
      );
  }
}
